import type { ReactNode } from 'react';
import { UMIcon } from '../../urbanmap/_components';
import { PLAN, UMR } from '../proposition-c/tokens';
import { CARD_HEIGHT } from './ProjectCard';
import { PlanLogo } from '../proposition-c/ui';

/** Rangée horizontale de cartes : titre, légende, action optionnelle, défilement horizontal. */
export function CardRow({
  title, hint, action, children,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section style={{ margin: '0 0 52px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, minHeight: action ? 42 : 40 }}>
        <h4 style={{ margin: 0, fontWeight: 400, fontSize: 24, lineHeight: '40px', color: UMR.textStrong }}>{title}</h4>
        {action}
      </div>
      {hint && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '16px', color: UMR.textSecondary, marginTop: 2 }}>
          <UMIcon name="info" size={14} color={UMR.textSecondary} />
          {hint}
        </div>
      )}
      <div style={{ display: 'flex', gap: 20, overflowX: 'auto', alignItems: 'flex-start', paddingBottom: 4, marginTop: 18 }}>{children}</div>
    </section>
  );
}

/**
 * Famille de projets : le projet d'origine et ses copies restent côte à côte,
 * sous un bandeau qui porte le nom de la famille.
 */
export function FamilyGroup({ name, count, children }: { name: string; count: number; children: ReactNode }) {
  const isFamily = count > 1;
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0, padding: isFamily ? '10px 12px 12px' : 0,
        border: isFamily ? `1px solid ${PLAN.accentBorder}` : 'none', background: isFamily ? PLAN.accentBg : 'transparent',
      }}
    >
      {isFamily && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '16px', color: PLAN.accentStrong, maxWidth: 540 }}>
          <UMIcon name="account_tree" size={14} color={PLAN.accent} />
          <span style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Famille · {name}</span>
          <span style={{ whiteSpace: 'nowrap' }}>({count} projets)</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: isFamily ? 12 : 20, alignItems: 'flex-start' }}>{children}</div>
    </div>
  );
}

/** Marque visuelle de filiation entre le projet d'origine et une de ses copies. */
export function FamilyLink() {
  return (
    <div aria-hidden style={{ display: 'flex', alignItems: 'center', height: CARD_HEIGHT, flexShrink: 0, color: PLAN.accent }}>
      <UMIcon name="subdirectory_arrow_right" size={18} color={PLAN.accent} />
    </div>
  );
}

/** En-tête Plan repris de « Création d'un projet — v2 ». */
export function HomeHeader() {
  return (
    <header
      style={{
        height: PLAN.headerHeight, minHeight: PLAN.headerHeight, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 16,
        padding: '0 24px', borderTop: `3px solid ${PLAN.accent}`, borderBottom: `1px solid ${UMR.border}`, background: UMR.white,
      }}
    >
      <PlanLogo size={36} />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: 15, fontWeight: 700, lineHeight: '20px', color: UMR.textStrong }}>Plan · Projets réseaux de chaleur</span>
        <span style={{ fontSize: 12, lineHeight: '16px', color: UMR.textSecondary }}>
          Bâtiments copiés depuis UrbanMap · synchronisation manuelle
        </span>
      </div>
    </header>
  );
}

export function EmptyState({ icon, title, children }: { icon: string; title: string; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '64px 24px', border: `1px dashed ${UMR.border}`, color: UMR.textSecondary, textAlign: 'center' }}>
      <UMIcon name={icon} size={40} color={UMR.textSecondary} />
      <div style={{ fontSize: 18, fontWeight: 700, color: UMR.textStrong }}>{title}</div>
      {children}
    </div>
  );
}
